/**
 * WatermarkRecommendationPanel Component
 *
 * Displays AI/analysis recommendations for watermark column selection
 */

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Alert, AlertDescription, AlertTitle } from '../ui/alert';
import { CheckCircle2, AlertCircle, Info, Lightbulb, Clock } from 'lucide-react';

interface WatermarkRecommendationPanelProps {
  tableName: string;
  recommendedColumn: string | null;
  allPotentialColumns: string | null;
  tableInfo?: any;
}

export function WatermarkRecommendationPanel({
  tableName,
  recommendedColumn,
  allPotentialColumns,
  tableInfo
}: WatermarkRecommendationPanelProps) {
  // Parse potential columns
  const potentialColumnsList = allPotentialColumns
    ? allPotentialColumns.split(',').map(col => col.trim())
    : [];

  return (
    <div className="space-y-6">
      {/* Recommendation Card */}
      {recommendedColumn ? (
        <Alert className="border-green-200 bg-green-50 dark:bg-green-900/20">
          <CheckCircle2 className="h-5 w-5 text-green-600" />
          <AlertTitle className="text-green-900 dark:text-green-100">
            Recommended Watermark Column
          </AlertTitle>
          <AlertDescription className="text-green-800 dark:text-green-200">
            <div className="mt-2 space-y-2">
              <div>
                <code className="bg-green-100 dark:bg-green-900/40 px-2 py-1 rounded text-sm font-mono">
                  {recommendedColumn}
                </code>
              </div>
              <p className="text-sm">
                {tableInfo?.reason || 'This column is suitable for incremental sync tracking.'}
              </p>
            </div>
          </AlertDescription>
        </Alert>
      ) : (
        <Alert className="border-red-200 bg-red-50 dark:bg-red-900/20">
          <AlertCircle className="h-5 w-5 text-red-600" />
          <AlertTitle className="text-red-900 dark:text-red-100">
            No Watermark Column Available
          </AlertTitle>
          <AlertDescription className="text-red-800 dark:text-red-200">
            <p className="mt-2 text-sm">
              {tableInfo?.reason || 'This table does not have suitable date/timestamp columns for incremental sync.'}
            </p>
            <p className="mt-2 text-sm font-medium">
              Recommendation: Switch to FULL sync mode for this table.
            </p>
          </AlertDescription>
        </Alert>
      )}

      {/* Additional Information */}
      <div className="grid gap-4 md:grid-cols-2">
        {/* Table Information */}
        <Card>
          <CardHeader>
            <CardTitle className="text-sm flex items-center gap-2">
              <Info className="h-4 w-4" />
              Table Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Table Name:</span>
              <code className="font-mono text-xs">{tableName}</code>
            </div>
            {tableInfo && (
              <>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Row Count:</span>
                  <span className="font-medium">{tableInfo.rowCount.toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Sync Type:</span>
                  <Badge variant="outline">{tableInfo.syncType}</Badge>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Is Active:</span>
                  <Badge variant={tableInfo.isActive ? 'default' : 'secondary'}>
                    {tableInfo.isActive ? 'Yes' : 'No'}
                  </Badge>
                </div>
                {tableInfo.lastSyncDate && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      Last Sync:
                    </span>
                    <span className="text-xs">
                      {new Date(tableInfo.lastSyncDate).toLocaleString()}
                    </span>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* Potential Columns */}
        {potentialColumnsList.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-sm flex items-center gap-2">
                <Lightbulb className="h-4 w-4" />
                All Potential Columns
              </CardTitle>
              <CardDescription className="text-xs">
                Other date/timestamp columns in this table
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {potentialColumnsList.map((column, index) => (
                  <Badge
                    key={index}
                    variant="outline"
                    className={
                      column === recommendedColumn
                        ? 'bg-green-100 text-green-800 border-green-300'
                        : 'bg-muted'
                    }
                  >
                    <code className="font-mono text-xs">{column}</code>
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Guidance */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Watermark Column Best Practices</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm text-muted-foreground">
          <div className="flex gap-3">
            <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-foreground">Choose modification timestamps</p>
              <p className="text-xs">
                Columns like MODIFIED_ON, UPDATED_ON, or LAST_MODIFIED_DATE are ideal as they track
                when records change.
              </p>
            </div>
          </div>

          <div className="flex gap-3">
            <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-foreground">Ensure consistent updates</p>
              <p className="text-xs">
                The watermark column must be reliably updated whenever a record changes to avoid
                missing data in incremental syncs.
              </p>
            </div>
          </div>

          <div className="flex gap-3">
            <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-foreground">Avoid creation timestamps for updates</p>
              <p className="text-xs">
                CREATED_ON or CREATED_DATE columns won't capture updates to existing records, only
                new insertions.
              </p>
            </div>
          </div>

          <div className="flex gap-3">
            <Info className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-foreground">Full sync alternative</p>
              <p className="text-xs">
                If no suitable watermark column exists, consider switching to FULL sync mode which
                reloads the entire table on each run.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
